import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useIsAboveBreakpoint from '@/helpers/useIsAboveBreakpoint';
import { Dialog, DialogPanel, Transition } from '@headlessui/react';
import useLayoutStore from '@/stores/layout/layoutStore';
import DButton from '@/components/Global/DButton';
import clsx from 'clsx';
import Button from '@/shared/components/UI/Button';
import PreviewIcon from '@/shared/icons/PreviewIcon';
import ChevronLeftIcon from '@/shared/icons/ChevronLeftIcon';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import TestIcon from '@/shared/icons/TestIcon';
import StepProgress from '@/shared/icons/StepProgress';
import CheckIcon from '@/shared/icons/CheckIcon';
import useStepFlowStore from '@/stores/stepFlow/stepFlowStore';
import { CHATBOT_ONBOARDING_WORKFLOW_KEY } from '@/stores/stepFlow/chatbotOnboardingSteps';
import useChatbotOnboardingStepUiState from '@/hooks/useChatbotOnboardingStepUiState';
import { ChatbotOnboardingStepClickContext } from '@/contexts/ChatbotOnboardingStepClickContext';

/**
 * This layout is used on pages with two areas: `children` and `RightSidebar`.
 * On screens smaller than the `sm` breakpoint, the `RightSidebar` is hidden and
 * only appears when the `handleRightSidebar` function is triggered.
 * On screens larger than `sm`, the `RightSidebar` is displayed alongside the `children`,
 * with the `children` area always taking up the maximum available space.
 * The width of the `RightSidebar` is not standardized by this layout.
 */

/** Full-width strip matching UI/Steps onboarding card colors (not rounded). Shown above “Back to editing” when the preview dialog covers the main onboarding card. */
const MobileOnboardingPreviewBanner = ({ stepUi, onClick }) => {
  if (!stepUi) return null;

  const { stepTitle, stepDescription, isCompleted, currentStep, totalSteps } = stepUi;

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onClick();
              }
            }
          : undefined
      }
      className={clsx(
        'flex w-full flex-shrink-0 items-start gap-v3-ms px-v3-sm py-v3-xs font-inter-variable',
        'bg-[#1E1D30]',
        onClick && 'cursor-pointer select-none transition-colors active:opacity-90',
        isCompleted
          ? 'border-b border-v3-green-light shadow-[inset_0_-1px_0_0_rgba(134,239,172,0.35)]'
          : 'border-b border-[#2C2A46]'
      )}
    >
      <IconWrapper size={36} className="flex-shrink-0">
        {isCompleted ? (
          <CheckIcon variant="circle" className="h-full w-full text-v3-green-dark" />
        ) : (
          <StepProgress className="h-full w-full text-[#44416C]" currentStep={currentStep} totalSteps={totalSteps} />
        )}
      </IconWrapper>
      <div className="flex min-w-0 flex-1 flex-col gap-v3-xxs py-0.5">
        <p className="text-v3-h10 font-medium text-[#C4BBFF]">{stepTitle}</p>
        <p className="text-v3-h11 font-medium text-[#D1D5DB]">{stepDescription}</p>
      </div>
    </div>
  );
};

const LayoutRightSidebar = ({ children, RightSidebar, showSidebar = true, mainClassName = '', showPreviewButton = true, fitSidebarToViewport = false, previewButtonLabel = 'Preview', type, backLabel = 'Back to Editing' }) => {
  const { id: kbId } = useParams();
  const onOnboardingStepClickFromOutlet = useContext(ChatbotOnboardingStepClickContext);
  const onboardingStepUi = useChatbotOnboardingStepUiState(kbId);
  const previewBubblePage = useLayoutStore((state) => state.previewBubblePage);
  const bannerHeight = useLayoutStore((state) => state.bannerHeight);
  const setPreviewBubblePage = useLayoutStore(
    (state) => state.setPreviewBubblePage
  );
  const setIsInPreviewBubblePage = useLayoutStore(
    (state) => state.setIsInPreviewBubblePage
  );

  const isAboveSm = useIsAboveBreakpoint('sm');
  const isAbovePreview = useIsAboveBreakpoint('preview');
  const isOnboardingActive = useStepFlowStore((state) =>
    Object.values(state.flows).some(
      (flow) => flow.workflowKey === CHATBOT_ONBOARDING_WORKFLOW_KEY && flow.currentStepId !== null
    )
  );

  // TopBar is 60px, total offset = 60 + banner height
  const layoutHeight = isOnboardingActive ? `calc(100vh - ${154 + bannerHeight}px)` : `calc(100vh - ${60 + bannerHeight}px)`;

  const [showRightSidebarMobile, setShowRightSidebarMobile] = useState(false);
  const [isTallViewport, setIsTallViewport] = useState(false);

  const handleRightSidebar = () => {
    setShowRightSidebarMobile(!showRightSidebarMobile);
  };

  const handleMobileOnboardingBannerClick = useCallback(() => {
    setShowRightSidebarMobile(false);
    if (onOnboardingStepClickFromOutlet) {
      void onOnboardingStepClickFromOutlet();
    }
  }, [onOnboardingStepClickFromOutlet]);

  useEffect(() => {
    if (!isAbovePreview && previewBubblePage.inPage) {
      setPreviewBubblePage({ inPage: true, isOpen: true });
    }
  }, [isAbovePreview, previewBubblePage.inPage]);

  // Reset mobile sidebar state when breakpoint changes to prevent auto-opening
  useEffect(() => {
    if (!isAbovePreview) {
      setShowRightSidebarMobile(false);
    }
  }, [isAbovePreview]);

  useEffect(() => {
    const handleResize = () => {
      setIsTallViewport(window.innerHeight >= 850);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    return () => {
      setIsInPreviewBubblePage(false);
    };
  }, []);

  const sidebarHeightStyle = fitSidebarToViewport
    ? { height: isTallViewport ? 'auto' : layoutHeight, maxHeight: layoutHeight }
    : undefined;

  const shouldUseDesktopLayout = fitSidebarToViewport ? isAbovePreview : isAboveSm;

  if (shouldUseDesktopLayout) {
    return (
      <main
        className={clsx(
          'layout-right-sidebar flex flex-row lg:gap-v3-xxhuge md:gap-v3-xhuge',
          'w-full',
          mainClassName
        )}
        style={{ height: layoutHeight, maxHeight: layoutHeight }}
      >
        <div
          data-joyride-scroll-container
          className={clsx(
            'grow flex flex-col min-w-0',
            mainClassName?.includes('overflow-y-auto') ? '' : 'h-full overflow-hidden',
            fitSidebarToViewport && 'overflow-y-auto',
            'no-scrollbar',
            showSidebar
              ? 'w-full xl:w-[680px] max-w-[680px]'
              : 'w-full'
          )}
        >
          {typeof children === 'function'
            ? children(handleRightSidebar)
            : children}
        </div>
        <Transition show={showSidebar}>
          <aside 
            style={sidebarHeightStyle}
            className={clsx(
              "hidden preview:flex transition duration-200 grow-0 flex-shrink-0 w-full py-v3-sm",
              type === 'expand' ? 'max-w-[696px]' : 'max-w-[424px]',
              fitSidebarToViewport && "sidebar-fit-viewport w-full xl:w-[424px] h-full sticky top-0 flex flex-col overflow-hidden items-center bubble-preview-min-height:justify-center"
            )}
          >
            {isAbovePreview
              ? (typeof RightSidebar === 'function'
                  ? RightSidebar(handleRightSidebar)
                  : RightSidebar)
              : null}
          </aside>
        </Transition>
        {!isAbovePreview && showSidebar && showPreviewButton && RightSidebar && (
          <>
            {!showRightSidebarMobile && (
              <div className="fixed bottom-v3-sm left-v3-sm sm:left-auto sm:right-[80px] z-50 preview:hidden">
                <Button
                  icon={TestIcon}
                  label={previewButtonLabel}
                  variant="dark"
                  onClick={handleRightSidebar}
                  className="shadow-lg hover:shadow-xl !w-[calc(100vw-30px)] sm:!w-[128px] justify-center"
                />
              </div>
            )}
            <Dialog
              open={showRightSidebarMobile}
              onClose={setShowRightSidebarMobile}
              className="relative z-50"
            >
              <aside className="fixed inset-0 overflow-hidden">
                <DialogPanel
                  transition
                  className={clsx(
                    'pointer-events-auto relative w-screen transform transition duration-150 ease-in-out data-[closed]:opacity-0 sm:duration-200',
                    'flex flex-col h-full bg-white overflow-hidden'
                  )}
                >
                  <div
                    className="flex flex-shrink-0 flex-col"
                    style={bannerHeight > 0 ? { marginTop: `${bannerHeight}px` } : undefined}
                  >
                    {isOnboardingActive && onboardingStepUi && (
                      <MobileOnboardingPreviewBanner
                        stepUi={onboardingStepUi}
                        onClick={onOnboardingStepClickFromOutlet ? handleMobileOnboardingBannerClick : undefined}
                      />
                    )}
                    <div
                      className="
                        group flex h-v3-xxl flex-shrink-0 cursor-pointer items-center gap-v3-xs
                        border-b border-v3-card-border
                        px-v3-sm py-v3-xs
                      "
                      onClick={handleRightSidebar}
                    >
                      <button
                        type="button"
                        onClick={handleRightSidebar}
                        className="flex items-center transition-opacity group-hover:opacity-80"
                      >
                        <IconWrapper size={24}>
                          <ChevronLeftIcon className="text-v3-purple-primary group-hover:text-v3-purple-medium" />
                        </IconWrapper>
                      </button>
                      <span
                        className="
                          font-[Inter Variable]
                          text-[14px] font-medium leading-[20px]
                          text-v3-purple-primary group-hover:text-v3-purple-medium
                        "
                        style={{
                          fontFamily: '"Inter Variable", Inter, sans-serif',
                          fontStyle: 'normal'
                        }}
                      >
                        {backLabel}
                      </span>
                      <div></div>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      'flex-1 overflow-hidden',
                      'flex flex-col w-full',
                      type !== 'expand' && 'items-center',
                      'min-h-0',
                      type !== 'expand' && 'bubble-preview-min-height:justify-center',
                      'overflow-y-auto no-scrollbar'
                    )}
                  >
                    <div
                      className={clsx(
                        'w-full h-full',
                        type !== 'expand' && 'sm:max-w-[424px] sm:mx-auto sm:max-h-[calc(100vh-60px)] sm:bubble-preview-min-height:max-h-[728px]'
                      )}
                    >
                      {typeof RightSidebar === 'function'
                        ? RightSidebar(handleRightSidebar)
                        : RightSidebar}
                    </div>
                  </div>
                </DialogPanel>
              </aside>
            </Dialog>
          </>
        )}
      </main>
    );
  }

  return (
    <main className={clsx('h-full gap-v3-md', 'w-full', mainClassName)}>
      <div className="h-full flex flex-col gap-v3-ms">
        <Transition
          show={
            (previewBubblePage.inPage && !showRightSidebarMobile) ||
            !previewBubblePage.inPage
          }
        >
          <div
            className={clsx(
              'h-full',
              'flex flex-col',
              'overflow-y-auto no-scrollbar',
              'transition duration-200 data-[enter]:delay-200 data-[closed]:opacity-0 data-[closed]:-translate-x-1'
            )}
          >
            {typeof children === 'function'
              ? children(handleRightSidebar)
              : children}
          </div>
        </Transition>

        <Transition show={previewBubblePage.inPage && showRightSidebarMobile}>
          <div
            className={clsx(
              'h-full',
              'flex flex-col items-center',
              // Vertically center when height > 850px, otherwise allow natural flow
              'bubble-preview-min-height:justify-center',
              'overflow-y-auto no-scrollbar',
              'transition duration-200 data-[enter]:delay-200 data-[closed]:opacity-0 data-[closed]:-translate-x-1',
              // Responsive width: full width on mobile, max 424px on tablet/desktop
              'w-full sm:max-w-[424px]'
            )}
          >
            {typeof RightSidebar === 'function'
              ? RightSidebar(handleRightSidebar)
              : RightSidebar}
          </div>
        </Transition>

        {/* {previewBubblePage.inPage && showPreviewButton && (
          <div className="w-full flex justify-center pb-v3-sm">
            <Button
              onClick={handleRightSidebar}
              variant="secondary"
              size="lg"
              label={showRightSidebarMobile ? 'Back to editing' : 'Preview agent'}
            />
          </div>
        )} */}
      </div>
      {(!previewBubblePage.inPage || !isAbovePreview) && showSidebar && (
        <Dialog
          open={showRightSidebarMobile}
          onClose={setShowRightSidebarMobile}
          className="relative z-50"
        >
          <aside className="fixed inset-0 overflow-hidden">
            <DialogPanel
              transition
              className={clsx(
                'pointer-events-auto relative w-screen transform transition duration-150 ease-in-out data-[closed]:opacity-0 sm:duration-200',
                'flex flex-col h-full bg-white overflow-hidden'
              )}
            >
              <div
                className="flex flex-shrink-0 flex-col"
                style={bannerHeight > 0 ? { marginTop: `${bannerHeight}px` } : undefined}
              >
                {isOnboardingActive && onboardingStepUi && (
                  <MobileOnboardingPreviewBanner
                    stepUi={onboardingStepUi}
                    onClick={onOnboardingStepClickFromOutlet ? handleMobileOnboardingBannerClick : undefined}
                  />
                )}
                <div
                  className="
                    group flex h-v3-xxl flex-shrink-0 cursor-pointer items-center gap-v3-xs
                    border-b border-v3-card-border
                    px-v3-sm py-v3-xs
                  "
                  onClick={handleRightSidebar}
                >
                  <button
                    type="button"
                    onClick={handleRightSidebar}
                    className="flex items-center transition-opacity group-hover:opacity-80"
                  >
                    <IconWrapper size={24}>
                      <ChevronLeftIcon className="text-v3-purple-primary group-hover:text-v3-purple-medium" />
                    </IconWrapper>
                  </button>
                  <span
                    className="
                      font-[Inter Variable]
                      text-[14px] font-medium leading-[20px]
                      text-v3-purple-primary group-hover:text-v3-purple-medium
                    "
                    style={{
                      fontFamily: '"Inter Variable", Inter, sans-serif',
                      fontStyle: 'normal'
                    }}
                  >
                    {backLabel}
                  </span>
                  <div></div>
                </div>
              </div>

              <div
                className={clsx(
                  'flex-1 overflow-hidden',
                  'flex flex-col w-full',
                  type !== 'expand' && 'items-center',
                  'min-h-0',
                  type !== 'expand' && 'bubble-preview-min-height:justify-center',
                  'overflow-y-auto no-scrollbar'
                )}
              >
                <div
                  className={clsx(
                    'w-full h-full',
                    type !== 'expand' && 'sm:max-w-[424px] sm:mx-auto sm:max-h-[calc(100vh-60px)] sm:bubble-preview-min-height:max-h-[728px]'
                  )}
                >
                  {typeof RightSidebar === 'function'
                    ? RightSidebar(handleRightSidebar)
                    : RightSidebar}
                </div>
              </div>
            </DialogPanel>
          </aside>
        </Dialog>
      )}

      {/* Floating Preview Button - only when on the tab that has the sidebar (e.g. Settings), below preview breakpoint */}
      {(!previewBubblePage.inPage || !isAbovePreview) && showSidebar && !showRightSidebarMobile && RightSidebar && (
        <div className="fixed bottom-v3-sm left-v3-sm sm:left-auto sm:right-[80px] z-50 preview:hidden">
          <Button
            icon={TestIcon}
            label={previewButtonLabel}
            variant="dark"
            onClick={handleRightSidebar}
            className="shadow-lg hover:shadow-xl !w-[calc(100vw-30px)] sm:!w-[128px] justify-center"
          />
        </div>
      )}
    </main>
  );
};

export default LayoutRightSidebar;
